/**
 * Group-structure analysis for legal entities (ported from n27). Determines
 * whether each entity is connected to a common group root through an unbroken
 * ownership chain at or above the control threshold (Konzernprivileg,
 * § 2 Abs. 1 Nr. 7 KWG orientation).
 *
 * Pure module — no Firebase imports (unit-testable).
 */

import type { LegalEntity } from '../types';

/**
 * Majority control (>50 %) is the conservative default; we require >= 50.01 %
 * — i.e. strictly more than half.
 */
export const GROUP_CONTROL_THRESHOLD_BPS = 5001;

export type GroupQualification = {
  /** Root entity of the qualified group, or null when the chain is broken. */
  rootId: string | null;
  /** True when the entity belongs to a qualified group. */
  qualified: boolean;
};

/**
 * Computes, per entity, whether it belongs to a qualified group and which root
 * anchors that group. Entities qualify when every ownership link on the path to
 * their root meets the control threshold. Roots (no parent) qualify by definition.
 * Broken chains — missing/archived parent, sub-threshold ownership, cycles —
 * disqualify the entity and everything below it.
 */
export function computeGroupQualification(
  entities: LegalEntity[]
): Record<string, GroupQualification> {
  const byId = new Map(entities.map((e) => [e.id, e]));
  const result: Record<string, GroupQualification> = {};

  const resolve = (id: string, visiting: Set<string>): GroupQualification => {
    if (result[id]) return result[id];
    if (visiting.has(id)) {
      // Ownership cycle — invalid data, nothing in the cycle qualifies.
      return { rootId: null, qualified: false };
    }
    visiting.add(id);

    const entity = byId.get(id);
    let qualification: GroupQualification;
    if (!entity) {
      qualification = { rootId: null, qualified: false };
    } else if (!entity.parentEntityId) {
      qualification = { rootId: id, qualified: true };
    } else if (!byId.has(entity.parentEntityId)) {
      // Dangling parent reference (archived/removed parent) — chain broken.
      qualification = { rootId: null, qualified: false };
    } else if ((entity.ownershipBps ?? 0) < GROUP_CONTROL_THRESHOLD_BPS) {
      qualification = { rootId: null, qualified: false };
    } else {
      const parent = resolve(entity.parentEntityId, visiting);
      qualification = parent.qualified
        ? { rootId: parent.rootId, qualified: true }
        : { rootId: null, qualified: false };
    }

    visiting.delete(id);
    result[id] = qualification;
    return qualification;
  };

  for (const entity of entities) {
    resolve(entity.id, new Set());
  }
  return result;
}

/** Returns the ids of all descendants of `entityId` (used to prevent cycle-creating parent picks). */
export function collectDescendantIds(entities: LegalEntity[], entityId: string): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const entity of entities) {
    if (!entity.parentEntityId) continue;
    const list = childrenByParent.get(entity.parentEntityId) ?? [];
    list.push(entity.id);
    childrenByParent.set(entity.parentEntityId, list);
  }
  const descendants = new Set<string>();
  const queue = [entityId];
  while (queue.length > 0) {
    const current = queue.pop()!;
    for (const child of childrenByParent.get(current) ?? []) {
      if (!descendants.has(child)) {
        descendants.add(child);
        queue.push(child);
      }
    }
  }
  return descendants;
}
